/**
 * MemoizedAircraftLabel — Per-aircraft label content, memoized on data only
 *
 * This component only re-renders when label lines change (data mutation),
 * NOT on camera movement. Position updates are applied imperatively via
 * the forwarded ref's style.transform.
 */

import { memo, forwardRef } from 'react'
import { Fuel, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { STATUS_ROW_ICON_MAP } from '@/lib/utils/status-row-icons'
import type { LabelLine } from '@/lib/utils/format-ramp-labels'
import type { StatusRowSeverity } from '@/types/aircraft-card'

// ── Severity badge styles (full mode) ──

const SEVERITY_BADGE_STYLES: Record<StatusRowSeverity, string> = {
  critical: 'bg-red-500/90 text-white border-red-600 hover:bg-red-500',
  warning: 'bg-orange-500/90 text-white border-orange-600 hover:bg-orange-500',
  notice: 'bg-yellow-500/90 text-yellow-950 border-yellow-600 hover:bg-yellow-500',
  info: 'bg-blue-500/90 text-white border-blue-600 hover:bg-blue-500',
  ok: 'bg-green-500/90 text-white border-green-600 hover:bg-green-500',
}

// ── Severity circle background styles (compact mode) ──

const SEVERITY_CIRCLE_STYLES: Record<StatusRowSeverity, string> = {
  critical: 'bg-red-500 text-white',
  warning: 'bg-orange-500 text-white',
  notice: 'bg-yellow-500 text-yellow-950',
  info: 'bg-blue-500 text-white',
  ok: 'bg-green-500 text-white',
}

// ── Presence badge styles (ghost aircraft state pills) ──

const PRESENCE_BADGE_STYLES: Record<string, string> = {
  away: 'bg-gray-400/80 text-white border-gray-500 hover:bg-gray-400',
  incoming: 'bg-blue-400/80 text-white border-blue-500 hover:bg-blue-400',
  returning: 'bg-emerald-500/80 text-white border-emerald-600 hover:bg-emerald-500',
}

interface MemoizedAircraftLabelProps {
  lines: LabelLine[]
  isGhost: boolean
}

export const MemoizedAircraftLabel = memo(
  forwardRef<HTMLDivElement, MemoizedAircraftLabelProps>(
    function MemoizedAircraftLabel({ lines, isGhost }, ref) {
      const presenceLines = lines.filter((l) => l.type === 'presence')
      const fullWarningLines = lines.filter((l) => l.type === 'warning')
      const compactWarningLines = lines.filter((l) => l.type === 'compact-warning')
      const dataLines = lines.filter((l) => l.type === 'data')

      return (
        <div
          ref={ref}
          className="absolute left-0 top-0"
          style={{
            contain: 'layout style paint',
            willChange: 'transform',
            // Initial position offscreen — set imperatively by the RAF loop
            transform: 'translate3d(-9999px, -9999px, 0)',
          }}
        >
          <div
            className={cn(
              'flex flex-col items-center gap-1',
              isGhost && 'opacity-70'
            )}
          >
            {/* Presence badges — ghost state pills */}
            {presenceLines.length > 0 && (
              <div className="flex flex-wrap justify-center gap-0.5">
                {presenceLines.map((line, i) => (
                  <Tooltip key={`pres-${i}`}>
                    <TooltipTrigger asChild>
                      <Badge
                        className={cn(
                          'text-[10px] leading-none px-1.5 py-0 h-4 rounded-sm pointer-events-auto cursor-default border shadow-sm flex items-center gap-1',
                          line.presenceMode &&
                            PRESENCE_BADGE_STYLES[line.presenceMode]
                        )}
                      >
                        {line.presenceMode === 'returning' && (
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                        )}
                        {line.value}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="text-[11px]">
                      {line.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Full-text warning badges */}
            {fullWarningLines.length > 0 && (
              <div className="flex flex-wrap justify-center gap-0.5">
                {fullWarningLines.map((line, i) => (
                  <Tooltip key={`warn-${i}`}>
                    <TooltipTrigger asChild>
                      <Badge
                        className={cn(
                          'text-[10px] leading-none px-1.5 py-0 h-4 rounded-sm pointer-events-auto cursor-default border shadow-sm',
                          SEVERITY_BADGE_STYLES[line.severity!]
                        )}
                      >
                        {line.value}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      className="text-[11px] space-y-0.5 max-w-[220px]"
                    >
                      <div className="font-medium">{line.value}</div>
                      {line.tooltipLines?.map((tl, j) => (
                        <div key={j} className="text-muted-foreground">
                          {tl}
                        </div>
                      ))}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Compact icon-circle warnings */}
            {compactWarningLines.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1">
                {compactWarningLines.map((line, i) => {
                  const IconComp = line.iconName
                    ? STATUS_ROW_ICON_MAP[line.iconName] || AlertTriangle
                    : AlertTriangle
                  return (
                    <Tooltip key={`compact-${i}`}>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'w-5 h-5 rounded-full flex items-center justify-center pointer-events-auto cursor-default shadow-sm',
                            SEVERITY_CIRCLE_STYLES[line.severity!]
                          )}
                        >
                          <IconComp className="w-3 h-3" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="right"
                        className="text-[11px] space-y-0.5 max-w-[220px]"
                      >
                        <div className="font-medium">{line.value}</div>
                        {line.tooltipLines?.map((tl, j) => (
                          <div key={j} className="text-muted-foreground">
                            {tl}
                          </div>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
            )}

            {/* Data labels — white card container */}
            {dataLines.length > 0 && (
              <div className="px-1.5 py-0.5 bg-background/95 rounded shadow-sm flex flex-col items-center gap-0.5">
                {dataLines.map((line, i) => (
                  <Tooltip key={`data-${i}`}>
                    <TooltipTrigger asChild>
                      {line.progress ? (
                        <div className="flex items-center gap-1 pointer-events-auto cursor-default min-w-[120px]">
                          <Fuel className="w-3 h-3 text-muted-foreground shrink-0" />
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={cn(
                                'h-full rounded-full transition-all',
                                line.progress.atRisk
                                  ? 'bg-amber-500'
                                  : line.progress.percent >= 100
                                    ? 'bg-green-500'
                                    : 'bg-primary'
                              )}
                              style={{
                                width: `${Math.min(100, line.progress.percent)}%`,
                              }}
                            />
                          </div>
                          <span className="text-[10px] tabular-nums shrink-0">
                            {line.progress.percent}%
                          </span>
                        </div>
                      ) : (
                        <div className="text-center text-xs leading-tight truncate max-w-[130px] pointer-events-auto cursor-default">
                          {line.value}
                        </div>
                      )}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="text-[11px]">
                      {line.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        </div>
      )
    }
  )
)
